const MANIFEST_URL = '/assets/members/mix-manifest.json';
const STORAGE_KEY = 'nautique_members_asset_version';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

function membersVersionFromManifest(manifest) {
    if (!manifest || typeof manifest !== 'object') {
        return null;
    }

    return [
        manifest['/js/app.js'],
        manifest['/js/vendor.js'],
        manifest['/js/manifest.js'],
        manifest['/css/app.css'],
    ].filter(Boolean).join('|');
}

export async function fetchAssetVersion() {
    const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error('Unable to fetch asset manifest');
    }

    const manifest = await response.json();

    return membersVersionFromManifest(manifest);
}

export function getStoredAssetVersion() {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
        return null;
    }
}

export function storeAssetVersion(version) {
    if (!version) {
        return;
    }

    try {
        window.sessionStorage.setItem(STORAGE_KEY, version);
    } catch (e) {
        // Ignore private-mode / quota errors
    }
}

/**
 * Treat the currently live manifest as the baseline for this page load.
 * After a refresh the loaded assets already match this version.
 */
export async function syncAssetVersionBaseline() {
    const version = await fetchAssetVersion();
    storeAssetVersion(version);

    return version;
}

export async function hasNewerAssetVersion() {
    const current = getStoredAssetVersion();
    const latest = await fetchAssetVersion();

    if (!current || !latest) {
        return false;
    }

    return current !== latest;
}

export function startAssetVersionMonitor({ onUpdate, intervalMs = CHECK_INTERVAL_MS } = {}) {
    let stopped = false;
    let timer = null;
    let ready = false;

    const check = async () => {
        if (stopped || !ready || document.visibilityState === 'hidden') {
            return;
        }

        try {
            if (await hasNewerAssetVersion()) {
                if (typeof onUpdate === 'function') {
                    onUpdate();
                }
            }
        } catch (e) {
            // Network blips should not surface as UI errors
        }
    };

    const onVisibility = () => {
        if (document.visibilityState === 'visible') {
            check();
        }
    };

    syncAssetVersionBaseline()
        .then(() => {
            ready = true;
        })
        .catch(() => {
            ready = true;
        });

    timer = window.setInterval(check, intervalMs);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
        stopped = true;

        if (timer) {
            window.clearInterval(timer);
        }

        document.removeEventListener('visibilitychange', onVisibility);
    };
}
